import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Upload, Clock } from 'lucide-react';

const EXCHANGES = [
  { id: 'mexc-futures', label: 'MEXC Futuros' },
  { id: 'mexc-spot', label: 'MEXC Spot' },
  { id: 'quantfury', label: 'Quantfury' },
  { id: 'otros', label: 'Otros' },
];

const MexcFuturesGuide = () => (
  <div className="space-y-6">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-sm">1</div>
      <div className="flex-1">
        <h4 className="text-white font-medium mb-1">Accede a la sección de Órdenes</h4>
        <p className="text-gray-400 text-sm leading-relaxed mb-3">
          Desde la interfaz de Futuros de MEXC, haz clic en el botón <strong className="text-gray-200">"Órdenes"</strong> situado en la esquina superior derecha de la pantalla.
        </p>
        <img src="/guides/mexc-futures-1.png" alt="MEXC Futuros - Paso 1" className="rounded-lg border border-gray-800 w-full" loading="lazy" />
      </div>
    </div>

    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-sm">2</div>
      <div className="flex-1">
        <h4 className="text-white font-medium mb-1">Abre el historial de operaciones</h4>
        <p className="text-gray-400 text-sm leading-relaxed mb-3">
          Se abrirá un menú desplegable. Selecciona la opción <strong className="text-gray-200">"Historial de órdenes y operaciones"</strong>.
        </p>
        <img src="/guides/mexc-futures-2.png" alt="MEXC Futuros - Paso 2" className="rounded-lg border border-gray-800 max-w-sm" loading="lazy" />
      </div>
    </div>

    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-sm">3</div>
      <div className="flex-1">
        <h4 className="text-white font-medium mb-1">Exporta el historial</h4>
        <p className="text-gray-400 text-sm leading-relaxed mb-3">
          En la pantalla del historial, localiza y haz clic en <strong className="text-gray-200">"Exportar historial de órdenes"</strong> en la parte superior derecha de la tabla.
        </p>
        <img src="/guides/mexc-futures-3.png" alt="MEXC Futuros - Paso 3" className="rounded-lg border border-gray-800 w-full" loading="lazy" />
      </div>
    </div>

    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-sm">4</div>
      <div className="flex-1">
        <h4 className="text-white font-medium mb-1">Selecciona el periodo y descarga</h4>
        <p className="text-gray-400 text-sm leading-relaxed mb-3">
          Se abrirá una ventana donde podrás elegir el rango de fechas deseado. Selecciona el periodo y confirma la exportación. El archivo se descargará en formato <strong className="text-gray-200">XLS (Excel)</strong>.
        </p>
        <img src="/guides/mexc-futures-5.png" alt="MEXC Futuros - Paso 4" className="rounded-lg border border-gray-800 max-w-lg" loading="lazy" />
      </div>
    </div>

    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-sm">5</div>
      <div className="flex-1">
        <h4 className="text-white font-medium mb-1">Sube el archivo a TraceMetrics</h4>
        <p className="text-gray-400 text-sm leading-relaxed">
          Guarda el archivo descargado en tu dispositivo y utiliza el botón <strong className="text-gray-200">"Seleccionar Archivo"</strong> de arriba para subirlo. TraceMetrics analizará tus operaciones automáticamente.
        </p>
      </div>
    </div>
  </div>
);

const ComingSoonGuide = ({ exchange }) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <Clock className="w-12 h-12 text-gray-600 mb-4" />
    <h4 className="text-white font-medium mb-2">Guía en preparación</h4>
    <p className="text-gray-500 text-sm max-w-md leading-relaxed">
      Estamos preparando la guía paso a paso para exportar tu historial de operaciones desde <strong className="text-gray-400">{exchange}</strong>. Estará disponible muy pronto.
    </p>
    <p className="text-gray-600 text-xs mt-4">
      Mientras tanto, puedes subir tu archivo directamente y lo analizaremos.
    </p>
  </div>
);

const OtrosGuide = () => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <Upload className="w-12 h-12 text-amber-500/60 mb-4" />
    <h4 className="text-white font-medium mb-2">¿Usas otro exchange?</h4>
    <p className="text-gray-400 text-sm max-w-md leading-relaxed mb-4">
      Estamos trabajando continuamente para integrar nuevos exchanges. Exporta el historial de operaciones desde tu plataforma (en formato <strong className="text-gray-200">Excel</strong> o <strong className="text-gray-200">PDF</strong>) y súbelo directamente.
    </p>
    <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 max-w-md">
      <p className="text-cyan-400 text-sm leading-relaxed">
        Nuestro equipo revisará tu archivo para añadir soporte a tu exchange. Te notificaremos por correo electrónico en cuanto esté disponible.
      </p>
    </div>
  </div>
);

const ExchangeGuide = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('mexc-futures');

  return (
    <div className="mt-8 w-full max-w-3xl mx-auto" data-testid="exchange-guide">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 mx-auto text-gray-400 hover:text-cyan-400 transition-colors text-sm"
        data-testid="exchange-guide-toggle"
      >
        <HelpCircle className="w-4 h-4" />
        ¿Cómo obtener tu archivo de operaciones?
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {isOpen && (
        <div className="mt-4 bg-[#0d0d0f] border border-gray-800 rounded-2xl overflow-hidden">
          <div className="flex border-b border-gray-800 overflow-x-auto">
            {EXCHANGES.map((ex) => (
              <button
                key={ex.id}
                onClick={() => setActiveTab(ex.id)}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === ex.id
                    ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/5'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
                data-testid={`guide-tab-${ex.id}`}
              >
                {ex.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'mexc-futures' && <MexcFuturesGuide />}
            {activeTab === 'mexc-spot' && <ComingSoonGuide exchange="MEXC Spot" />}
            {activeTab === 'quantfury' && <ComingSoonGuide exchange="Quantfury" />}
            {activeTab === 'otros' && <OtrosGuide />}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeGuide;
